// Định tuyến tối giản dựa trên History API.
//
// VÌ SAO KHÔNG react-router:
// App chỉ có 9 màn và một luồng tuần tự, không có tham số động phức tạp, không
// có layout chung, không có loader. react-router thêm ~20KB (kèm react-router-dom
// và matchPath) cho những thứ ở đây gói trong 30 dòng.
//
// NHƯNG vẫn phải có router. Trước đây dùng máy trạng thái đơn thuần, và hậu
// quả thấy ngay:
//   - Bấm nút back của trình duyệt không quay lại màn trước.
//   - F5 giữa khi làm bài là mất trắng phiên đang làm.
//   - Không bookmark được, không gửi link cho ai được.
// Đó là ba lỗi người dùng nhận ra ngay trong lần dùng đầu, không phải lỗi kỹ
// thuật bên trong.

import { useCallback, useEffect, useState } from 'react'

export function useRoute() {
  const [path, setPath] = useState(() => window.location.pathname)

  useEffect(() => {
    const onPop = () => setPath(window.location.pathname)
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  // `replace` dùng cho chuyển hướng thay thế (vd chưa đăng nhập mà mở
  // /progress → đưa về /login). `push` cho điều hướng thật, để back hoạt động.
  const go = useCallback((to: string, replace = false) => {
    if (to === window.location.pathname) return
    window.history[replace ? 'replaceState' : 'pushState']({}, '', to)
    setPath(to)
  }, [])

  return { path, go }
}

/** Bỏ dấu / cuối, trừ / gốc — để /home và /home/ là một đường dẫn. */
export function norm(p: string): string {
  if (p.length > 1 && p.endsWith('/')) return p.slice(0, -1)
  return p
}

export type View =
  | 'welcome'
  | 'login'
  | 'register'
  | 'home'
  | 'topic'
  | 'count'
  | 'practice'
  | 'exam'
  | 'examSetup'
  | 'result'
  | 'progress'
  | 'wrong'
  | 'pay'
  | 'account'

/** Đường dẫn của từng màn. Một chỗ duy nhất, không rải chuỗi '/' trong App. */
export const P = {
  welcome: '/',
  login: '/login',
  register: '/register',
  home: '/home',
  topic: '/topics',
  count: '/count',
  practice: '/practice', // + /:sessionId
  exam: '/exam',
  examSetup: '/exam/setup',
  result: '/result',
  progress: '/progress',
  wrong: '/wrong',
  pay: '/pay',
  account: '/account',
} as const

/** Màn nào từ đường dẫn.
 *
 *  ĐƯỜNG DẪN NHIỀU PHẦN PHẢI KHỚP TRỌN VẸN TRƯỚC.
 *
 *  Đã mắc đúng lỗi này và nó làm cả luồng thi thử chết: bản đầu chỉ so phần
 *  đầu `head = '/' + path.split('/')[1]`, mà `head` không bao giờ chứa dấu '/'. Nên
 *  `/exam/setup` rơi vào `case '/exam'` và mở LẠI màn chọn loại đề. Người dùng
 *  bấm "Tiếp tục với đề rút gọn" hoài mà không bao giờ tới màn "Bắt đầu thi".
 *  Trường hợp `case '/exam/setup'` viết ra trông rất hợp lý nhưng KHÔNG BAO GIỜ
 *  chạy được — TypeScript không bắt được, build vẫn xanh, chỉ chạy thật mới lộ.
 *
 *  `/practice/:id` thì phần sau là session id, tách riêng ở `sessionIDOf` nên
 *  so phần đầu là đúng.
 */
export function viewOf(path: string): View {
  const p = norm(path)
  if (p === P.examSetup) return 'examSetup'
  const head = '/' + p.split('/')[1]
  switch (head) {
    case P.login: return 'login'
    case P.register: return 'register'
    case P.home: return 'home'
    case P.topic: return 'topic'
    case P.count: return 'count'
    case P.practice: return 'practice'
    case P.exam: return 'exam'
    case P.result: return 'result'
    case P.progress: return 'progress'
    case P.wrong: return 'wrong'
    case P.pay: return 'pay'
    case P.account: return 'account'
    default: return 'welcome'
  }
}

/** Session id trong `/practice/:id`, rỗng nếu không có. */
export function sessionIDOf(path: string): string {
  const parts = norm(path).split('/')
  return parts[0] === '' && parts[1] === 'practice' ? (parts[2] ?? '') : ''
}
